import { useMemo, useState } from 'react'
import { Box, Divider, IconButton, InputAdornment, TextField, ThemeProvider, Typography, alpha, createTheme, useTheme } from '@mui/material'
import { ErrorOutlineRounded, VisibilityOffOutlined, VisibilityOutlined } from '@mui/icons-material'

import useDialogStyles from '../../hooks/useDialogStyles'
import { APP_VERSION } from '../../config/version'
import { FONT_MONO } from '../../config/fonts'

const COPYRIGHT_YEAR = new Date().getFullYear()

// Text field in the dialog style (gold focus, themed autofill)
export const AuthField = ({ sx, ...props }) => {
  const { fieldSx } = useDialogStyles()
  return <TextField fullWidth size="medium" margin="none" sx={{ ...fieldSx, ...sx }} {...props} />
}

// Password with a show / hide eye
export const PasswordField = (props) => {
  const theme = useTheme()
  const [visible, setVisible] = useState(false)
  return (
    <AuthField
      type={visible ? 'text' : 'password'}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              edge="end"
              size="small"
              onClick={() => setVisible((v) => !v)}
              onMouseDown={(e) => e.preventDefault()}
              aria-label={visible ? 'Hide password' : 'Show password'}
              sx={{ color: theme.palette.text.secondary }}
            >
              {visible ? <VisibilityOffOutlined fontSize="small" /> : <VisibilityOutlined fontSize="small" />}
            </IconButton>
          </InputAdornment>
        ),
      }}
      {...props}
    />
  )
}

// "──── or ────"
export const OrDivider = () => {
  const theme = useTheme()
  return (
    <Divider sx={{ my: 2.5, fontSize: '0.75rem', color: theme.palette.text.secondary, '&::before, &::after': { borderColor: theme.palette.divider } }}>
      or
    </Divider>
  )
}

export const AuthError = ({ children }) => {
  const theme = useTheme()
  const color = theme.palette.error.main
  return (
    <Box
      role="alert"
      sx={{
        display: 'flex',
        gap: 1,
        mt: 2,
        p: 1.25,
        borderRadius: 2,
        fontSize: '0.83rem',
        color: theme.palette.mode === 'dark' ? theme.palette.error.light : theme.palette.error.dark,
        backgroundColor: alpha(color, 0.1),
        border: `1px solid ${alpha(color, 0.4)}`,
        '& ul': { m: '4px 0 0', pl: 2.25 },
      }}
    >
      <ErrorOutlineRounded sx={{ fontSize: 18, mt: '1px', flexShrink: 0 }} />
      <Box>{children}</Box>
    </Box>
  )
}

// Copyright + version, same quiet grey as the sidebar footer.
// onDark: over the dark background picture, so light grey in both themes.
export const AuthFooterNote = ({ onDark = false, sx }) => {
  const theme = useTheme()
  const isDark = onDark || theme.palette.mode === 'dark'
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        gap: 1.5,
        fontSize: '0.72rem',
        color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.5)',
        userSelect: 'none',
        ...sx,
      }}
    >
      <span>© Copyright {COPYRIGHT_YEAR}</span>
      <span>·</span>
      <Box component="span" sx={{ fontFamily: FONT_MONO, color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.65)' }}>
        v{APP_VERSION}
      </Box>
    </Box>
  )
}

// Colours of the background picture (deep navy) and of the glass panels on Home
const GLASS = 'rgba(6, 18, 36, 0.62)'
const NAVY = '#0a1a2f'

/**
 * The auth pages always sit on the dark background picture, whatever the app theme is.
 * A nested dark theme in the picture's navy makes every field, button, divider and the Google
 * dialog match it, without special cases in each component.
 */
const useAuthTheme = () => {
  const outer = useTheme()
  return useMemo(() => createTheme(outer, {
    palette: {
      mode: 'dark',
      background: { default: NAVY, paper: NAVY, alt: NAVY },
      text: { primary: '#f3f6fa', secondary: 'rgba(255,255,255,0.66)' },
      divider: 'rgba(255,255,255,0.12)',
    },
  }), [outer])
}

/**
 * Shell of the Login / Signup pages: brand on top, a glass card over the background picture
 * (same glass as the Home feature cards, gold accent line) and the copyright line below.
 */
const AuthCard = (props) => (
  <ThemeProvider theme={useAuthTheme()}>
    <AuthCardBody {...props} />
  </ThemeProvider>
)

const AuthCardBody = ({ icon: Icon, title, subtitle, children, maxWidth = 420 }) => {
  const theme = useTheme()
  const accent = theme.palette.secondary.main

  return (
    <Box
      sx={{
        minHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        py: { xs: 3, sm: 5 },
      }}
    >
      {/* Brand */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 2.5, userSelect: 'none' }}>
        <Box
          component="img"
          src="/assets/simbalinkicon.png"
          alt="SimbaLink"
          sx={{ width: 40, height: 40, borderRadius: '11px', boxShadow: `0 0 0 1px ${alpha(accent, 0.4)}, 0 4px 14px rgba(0,0,0,0.35)` }}
        />
        <Typography sx={{ fontWeight: 800, fontSize: '1.5rem', letterSpacing: '0.08em', color: accent, textShadow: '0 2px 8px rgba(0,0,0,0.45)' }}>
          SIMBALINK
        </Typography>
      </Box>

      {/* Card */}
      <Box
        sx={{
          width: '100%',
          maxWidth,
          position: 'relative',
          overflow: 'hidden',
          borderRadius: 3,
          // glass: the picture shows through, blurred
          backgroundColor: GLASS,
          backdropFilter: 'blur(14px) saturate(120%)',
          WebkitBackdropFilter: 'blur(14px) saturate(120%)',
          border: '1px solid rgba(255,255,255,0.12)',
          boxShadow: '0 24px 60px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.06)',
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: '0 0 auto 0',
            height: 3,
            background: `linear-gradient(90deg, ${alpha(accent, 0)} 0%, ${accent} 50%, ${alpha(accent, 0)} 100%)`,
          },
        }}
      >
        <Box sx={{ px: { xs: 3, sm: 4 }, pt: 3.5, pb: 3 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
            {Icon && (
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  flexShrink: 0,
                  display: 'grid',
                  placeItems: 'center',
                  borderRadius: '12px',
                  color: accent,
                  backgroundColor: alpha(accent, 0.12),
                  border: `1px solid ${alpha(accent, 0.3)}`,
                }}
              >
                <Icon />
              </Box>
            )}
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 700, fontSize: '1.3rem', lineHeight: 1.2, color: theme.palette.text.primary }}>
                {title}
              </Typography>
              {subtitle && (
                <Typography sx={{ mt: 0.25, fontSize: '0.85rem', color: theme.palette.text.secondary }}>
                  {subtitle}
                </Typography>
              )}
            </Box>
          </Box>

          {children}
        </Box>
      </Box>

      <AuthFooterNote onDark sx={{ mt: 3 }} />
    </Box>
  )
}

export default AuthCard
